import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3,
  Bell,
  Building2,
  ChevronLeft,
  Home,
  Inbox,
  Mail,
  Layers,
  ListChecks,
  Loader,
  LogOut,
  Mails,
  Menu,
  MoreHorizontal,
  Send,
  Settings,
  X,
} from 'lucide-react';
import { messages } from '@nesc/shared';
import { api, type ListResponse, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { useAuth } from '@/lib/auth';
import { usePollingInterval } from '@/lib/hooks';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { QuotaBanner } from '@/components/common/quota-banner';
import { NotificationsPanel } from '@/components/common/notifications-panel';

type NavItem = {
  to: string;
  label: string;
  /** スマホ下部タブでの表記。折り返しを避けるため短くする */
  shortLabel?: string;
  icon: typeof Home;
  /** スマホの下部タブに出すか */
  mobile?: boolean;
  minRole?: 'manager' | 'admin';
};

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: messages.nav.home, icon: Home, mobile: true },
  { to: '/companies', label: messages.nav.companies, icon: Building2, mobile: true },
  { to: '/lists', label: messages.nav.lists, icon: Layers },
  {
    to: '/campaigns',
    label: messages.nav.campaigns,
    shortLabel: messages.nav.campaignsShort,
    icon: Send,
    mobile: true,
  },
  { to: '/inbox', label: messages.nav.inbox, icon: Inbox, mobile: true },
  { to: '/mail', label: messages.nav.mail, icon: Mail },
  { to: '/newsletter', label: messages.nav.newsletter, icon: Mails },
  { to: '/appointments', label: messages.nav.appointments, icon: ListChecks },
  { to: '/analytics', label: messages.nav.analytics, icon: BarChart3 },
  { to: '/settings', label: messages.nav.settings, icon: Settings },
];

export const AppLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('sidebar-collapsed') === 'true',
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const { data: activeJobs } = useQuery({
    queryKey: qk.activeJobs,
    queryFn: () => api.get<SingleResponse<{ id: string; targetLabel: string | null; jobLabel: string; progressDone: number; progressTotal: number; href: string | null }[]>>('/jobs/active'),
    refetchInterval: usePollingInterval(true, 5000),
  });

  const { data: unread } = useQuery({
    queryKey: qk.unreadCount,
    queryFn: () => api.get<SingleResponse<{ count: number }>>('/notifications/unread-count'),
    refetchInterval: usePollingInterval(true, 30_000),
  });

  const { data: inboxCounts } = useQuery({
    queryKey: qk.inbox({ handlingStatus: 'open', limit: 1 }),
    queryFn: () =>
      api.get<ListResponse<unknown>>('/inbox', { handlingStatus: 'open', limit: 1 }),
    refetchInterval: usePollingInterval(true, 60_000),
  });

  const runningCount = activeJobs?.data.length ?? 0;
  const unreadCount = unread?.data.count ?? 0;
  const openReplies = (inboxCounts?.meta?.counts as { open?: number } | undefined)?.open ?? 0;

  const toggleSidebar = () => {
    setCollapsed((prev) => {
      localStorage.setItem('sidebar-collapsed', String(!prev));
      return !prev;
    });
  };

  const currentTitle =
    NAV_ITEMS.find((item) =>
      item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to),
    )?.label ?? messages.app.name;

  return (
    <div className="flex min-h-dvh bg-background">
      {/* PC：サイドバー */}
      <aside
        className={cn(
          'hidden shrink-0 border-r border-border bg-surface lg:flex lg:flex-col',
          collapsed ? 'w-sidebar-collapsed' : 'w-sidebar',
        )}
      >
        <div className={cn('flex h-14 items-center border-b border-border px-4', collapsed && 'justify-center px-2')}>
          {collapsed ? (
            <span className="text-h3" aria-label={messages.app.name}>
              営
            </span>
          ) : (
            <span className="text-h3 truncate">{messages.app.name}</span>
          )}
        </div>

        <nav className="flex-1 space-y-1 p-2" aria-label="主要メニュー">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-control px-3 py-2.5 text-body',
                  'transition-colors duration-hover',
                  isActive
                    ? 'bg-accent text-primary-foreground'
                    : 'text-foreground-muted hover:bg-muted hover:text-foreground',
                  collapsed && 'justify-center px-2',
                )
              }
              title={collapsed ? item.label : undefined}
            >
              <item.icon aria-hidden className="size-5 shrink-0" />
              {!collapsed && <span className="truncate">{item.label}</span>}
              {!collapsed && item.to === '/inbox' && openReplies > 0 && (
                <span className="tabular ml-auto rounded-control bg-foreground px-1.5 text-note text-primary-foreground">
                  {openReplies}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-border p-2">
          <Button
            variant="ghost"
            size={collapsed ? 'icon' : 'default'}
            className={cn('w-full', !collapsed && 'justify-start')}
            onClick={toggleSidebar}
            aria-label={collapsed ? messages.nav.expand : messages.nav.collapse}
          >
            <ChevronLeft aria-hidden className={cn('size-5 transition-transform', collapsed && 'rotate-180')} />
            {!collapsed && <span>{messages.nav.collapse}</span>}
          </Button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* 上部バー */}
        <header className="sticky top-0 z-30 flex h-bar-top shrink-0 items-center gap-2 border-b border-border bg-background px-3 lg:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMenuOpen(true)}
            aria-label="メニューを開く"
          >
            <Menu aria-hidden />
          </Button>

          {/* 画面見出しは PageHeader が h1 として持つため、ここは見出しにしない */}
          <p className="min-w-0 flex-1 truncate text-h3 lg:hidden">{currentTitle}</p>

          <div className="ml-auto flex items-center gap-1.5">
            {runningCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/jobs')}
                className="hidden lg:inline-flex"
              >
                <Loader aria-hidden className="size-4 animate-spin" />
                {messages.jobs.runningCount(runningCount)}
              </Button>
            )}

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setNotificationsOpen(true)}
              aria-label={`${messages.notifications.title}${unreadCount > 0 ? `（未読 ${unreadCount} 件）` : ''}`}
              className="relative"
            >
              <Bell aria-hidden />
              {unreadCount > 0 && (
                <span className="tabular absolute -right-0.5 -top-0.5 min-w-4 rounded-control bg-foreground px-1 text-note leading-4 text-primary-foreground">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="hidden lg:inline-flex"
              onClick={() => void logout().then(() => navigate('/login'))}
            >
              <LogOut aria-hidden className="size-4" />
              <span className="max-w-24 truncate">{user?.name ?? user?.email}</span>
            </Button>
          </div>
        </header>

        {/* 上限警告バナー（80% 超のときのみ） */}
        <QuotaBanner />

        {/* 実行中の細帯（スマホ） */}
        {runningCount > 0 && (
          <button
            type="button"
            onClick={() => navigate('/jobs')}
            className="flex min-h-touch w-full items-center gap-2 border-b border-border bg-muted px-3 py-1.5 text-note text-foreground lg:hidden"
          >
            <Loader aria-hidden className="size-4 shrink-0 animate-spin" />
            <span className="truncate">
              {activeJobs?.data[0]?.targetLabel ?? activeJobs?.data[0]?.jobLabel}
            </span>
            <span className="tabular ml-auto shrink-0">
              {activeJobs?.data[0]?.progressDone}/{activeJobs?.data[0]?.progressTotal}
            </span>
          </button>
        )}

        <main className="flex-1 px-4 py-4 pb-24 md:px-6 lg:px-8 lg:pb-8">
          <div className="mx-auto w-full max-w-content xl:max-w-content-xl">
            <Outlet />
          </div>
        </main>

        {/* スマホ：下部タブ */}
        <nav
          className="safe-bottom fixed inset-x-0 bottom-0 z-30 flex h-bar-bottom border-t border-border bg-background lg:hidden"
          aria-label="主要メニュー"
        >
          {NAV_ITEMS.filter((item) => item.mobile).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                cn(
                  'relative flex flex-1 flex-col items-center justify-center gap-0.5 text-note',
                  isActive ? 'font-semibold text-foreground' : 'text-foreground-muted',
                )
              }
            >
              <item.icon aria-hidden className="size-5" />
              <span className="max-w-full truncate px-0.5">{item.shortLabel ?? item.label}</span>
              {item.to === '/inbox' && openReplies > 0 && (
                <span className="tabular absolute right-[22%] top-1.5 min-w-4 rounded-control bg-foreground px-1 text-note leading-4 text-primary-foreground">
                  {openReplies > 99 ? '99+' : openReplies}
                </span>
              )}
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            className="flex flex-1 flex-col items-center justify-center gap-0.5 text-note text-foreground-muted"
          >
            <MoreHorizontal aria-hidden className="size-5" />
            <span>{messages.nav.more}</span>
          </button>
        </nav>
      </div>

      {/* スマホ：全メニュー */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-overlay"
            onClick={() => setMenuOpen(false)}
            aria-label="メニューを閉じる"
          />
          <div className="safe-bottom absolute inset-x-0 bottom-0 max-h-[85dvh] animate-slide-up overflow-y-auto rounded-t-dialog bg-surface-raised p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-h3">{messages.app.name}</p>
              <Button variant="ghost" size="icon" onClick={() => setMenuOpen(false)} aria-label={messages.common.close}>
                <X aria-hidden />
              </Button>
            </div>
            <nav className="space-y-1">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'flex min-h-touch items-center gap-3 rounded-control px-3 text-body',
                      isActive ? 'bg-accent text-primary-foreground' : 'text-foreground',
                    )
                  }
                >
                  <item.icon aria-hidden className="size-5" />
                  {item.label}
                </NavLink>
              ))}
              <NavLink
                to="/jobs"
                onClick={() => setMenuOpen(false)}
                className="flex min-h-touch items-center gap-3 rounded-control px-3 text-body text-foreground"
              >
                <Loader aria-hidden className="size-5" />
                {messages.nav.jobs}
              </NavLink>
            </nav>
            <div className="mt-3 border-t border-border pt-3">
              <Button
                variant="outline"
                size="lg"
                className="w-full"
                onClick={() => void logout().then(() => navigate('/login'))}
              >
                <LogOut aria-hidden />
                {messages.auth.logout}
              </Button>
            </div>
          </div>
        </div>
      )}

      <NotificationsPanel open={notificationsOpen} onOpenChange={setNotificationsOpen} />
    </div>
  );
};
